import React, {forwardRef} from 'react'
import {
  DropdownOptionProps,
  DropdownTheme,
  DatePickerInputFirstDayOfWeek,
  TextFieldTheme,
  DropdownInputHandle,
} from 'wix-ui-tpa/cssVars'
import {useEnvironment, useTranslation} from '@wix/yoshi-flow-editor'
import {useSettings} from '@wix/tpa-settings/react'

import {LayoutSize} from '../../../../components-shared/LayoutSizeProvider/types'
import {LazyDatePicker} from '../../../../components-shared/LazyDatePicker'
import {getFirstDayOfWeek, parseLocaleFromBM} from '../../../../utils/locale'
import settingsParams from '../../settingsParams'
import {Dropdown} from '../../../../components-shared/Dropdown'

import {classes, st} from './Form.st.css'

type DefaultOptions = DropdownOptionProps[]
type DefaultOnChange = (option: DropdownOptionProps) => void

interface Props {
  layoutSize: LayoutSize
  regionalSettings?: string
  partySize: {
    value: string
    options: DefaultOptions
    onChange: DefaultOnChange
  }
  date: {
    value?: Date
    filterDate: (date: Date) => boolean
    onChange: (date: Date) => void
  }
}

export const Form = forwardRef<DropdownInputHandle, Props>(
  ({layoutSize, partySize, date, regionalSettings}: Props, ref) => {
    const {t} = useTranslation()
    const {isSSR} = useEnvironment()
    const settings = useSettings()
    const locale = parseLocaleFromBM(regionalSettings)

    const weekFirstDay = getFirstDayOfWeek(locale) as DatePickerInputFirstDayOfWeek

    const textFieldTheme = settings.get(settingsParams.textFieldTheme)

    const isTextFieldThemeBox = textFieldTheme === TextFieldTheme.Box

    const textFieldThemeClass = isTextFieldThemeBox ? classes.box : classes.line

    const dropdownTheme = isTextFieldThemeBox ? DropdownTheme.Box : DropdownTheme.Line
    const datePickerTheme = isTextFieldThemeBox ? TextFieldTheme.Box : TextFieldTheme.Line

    return (
      <div className={st(classes.root, {[layoutSize]: true}, textFieldThemeClass)}>
        <Dropdown
          ref={ref}
          className={st(classes.dropdown)}
          upgrade
          label={t('uou-reservations.reservations.party')}
          initialSelectedId={partySize.value}
          options={partySize.options}
          onChange={partySize.onChange}
          theme={dropdownTheme}
        />

        <LazyDatePicker
          firstDayOfWeek={weekFirstDay}
          label={t('uou-reservations.reservations.date')}
          value={isSSR ? undefined : date.value}
          filterDate={date.filterDate}
          onChange={date.onChange}
          datePickerClass={classes.datePicker}
          dateTextFieldDataHook="experience-date-picker"
          excludePastDates
          inputTheme={datePickerTheme}
        />
      </div>
    )
  },
)
